import { Card } from "@acme/ui/card";
import { createFileRoute } from "@tanstack/react-router";
import { BarChart3, Shield, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import useClanDetailAccess from "~/features/clan-detail/_hooks/useClanDetailAccess.js";

export const Route = createFileRoute("/_authed/my-clans/$tag/")({
  component: DashboardTab,
});

function DashboardTab() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const { clanInfo, activeWar, isReady, canAccessClan } = useClanDetailAccess({ tag });

  if (!isReady || !canAccessClan) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Card className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <BarChart3 className="size-4" />
          <span className="text-sm font-medium">{t("app.placeholders.clanDashboardStats")}</span>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("app.placeholders.clanDashboardStatsHint")}</p>
        {clanInfo != null && (
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("app.myClans.table.level")}</dt>
              <dd className="font-medium tabular-nums">{clanInfo.clanLevel ?? "–"}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("app.myClans.table.members")}</dt>
              <dd className="font-medium tabular-nums">{clanInfo.members ?? "–"}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("app.myClans.table.warWins")}</dt>
              <dd className="font-medium tabular-nums">{clanInfo.warWins ?? "–"}</dd>
            </div>
          </dl>
        )}
      </Card>
      <Card className="p-4 sm:col-span-2 lg:col-span-1">
        <div className="flex items-center gap-2 text-muted-foreground">
          {activeWar?.state === "war" || activeWar?.state === "preparation" ? (
            <Zap className="size-4 text-destructive" />
          ) : (
            <Shield className="size-4" />
          )}
          <span className="text-sm font-medium">{t("app.clanDetail.activeWar")}</span>
        </div>
        {activeWar == null ? (
          <p className="mt-2 text-sm text-muted-foreground">{t("app.myClans.idle")}</p>
        ) : (
          <dl className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("app.clanDetail.statusLabel")}</dt>
              <dd className="font-medium capitalize">{activeWar.state}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("app.clanDetail.sizeLabel")}</dt>
              <dd className="font-medium tabular-nums">{activeWar.teamSize ?? "–"}</dd>
            </div>
          </dl>
        )}
      </Card>
    </div>
  );
}
